// Admin endpoints for the Benefits Intelligence feature (core-lambda).
// Contract: src/Pages/Admin/Corporates/BENEFITS_API.md.
// Auth is the standard admin bearer token already attached by the shared
// axios instance.

import { axiosAssessmentInstance } from "../AxiosConfiguration";

const unwrap = (response, fallback = null) =>
  response?.data?.payload ?? fallback;

// When the backend hasn't deployed a benefits endpoint yet, list-style routes
// 404. Treat that as "feature not provisioned → empty" rather than an error so
// the admin UI degrades gracefully. Re-throw any other status.
const swallow404 = (fallback) => (err) => {
  if (err?.response?.status === 404) return fallback;
  throw err;
};

const base = (cid) => `/api/admin/enterprises/${cid}/benefits`;

// ─── Corporate-owned benefits (CRUD) ────────────────────────────────────────

export const listBenefits = async (cid, { status, category } = {}) => {
  const response = await axiosAssessmentInstance
    .get(base(cid), { params: { status, category } })
    .catch(swallow404({ data: { payload: { items: [], total: 0 } } }));
  const payload = unwrap(response, {});
  return {
    items: payload?.items || [],
    total: payload?.total ?? (payload?.items?.length || 0),
  };
};

export const getBenefit = async (cid, bid) => {
  const response = await axiosAssessmentInstance.get(`${base(cid)}/${bid}`);
  return unwrap(response);
};

export const createBenefit = async (cid, body) => {
  const response = await axiosAssessmentInstance.post(base(cid), body);
  return unwrap(response);
};

export const updateBenefit = async (cid, bid, patch) => {
  const response = await axiosAssessmentInstance.patch(
    `${base(cid)}/${bid}`,
    patch
  );
  return unwrap(response);
};

export const activateBenefit = async (cid, bid) => {
  const response = await axiosAssessmentInstance.post(
    `${base(cid)}/${bid}/activate`
  );
  return unwrap(response);
};

export const archiveBenefit = async (cid, bid) => {
  const response = await axiosAssessmentInstance.post(
    `${base(cid)}/${bid}/archive`
  );
  return unwrap(response);
};

// ─── Global catalog (read-only browse) ──────────────────────────────────────

export const listGlobalCatalog = async (cid, { status = "active" } = {}) => {
  const response = await axiosAssessmentInstance
    .get(`${base(cid)}/global-catalog/list`, { params: { status } })
    .catch(swallow404({ data: { payload: { items: [], total: 0 } } }));
  const payload = unwrap(response, {});
  return {
    items: payload?.items || [],
    total: payload?.total ?? (payload?.items?.length || 0),
  };
};

// ─── Per-corporate enablement of globals ────────────────────────────────────

export const listEnablements = async (cid, { is_enabled } = {}) => {
  const response = await axiosAssessmentInstance
    .get(`${base(cid)}/enablements`, { params: { is_enabled } })
    .catch(swallow404({ data: { payload: { items: [], total: 0 } } }));
  const payload = unwrap(response, {});
  return {
    items: payload?.items || [],
    total: payload?.total ?? (payload?.items?.length || 0),
  };
};

export const enableGlobalBenefit = async (cid, globalBenefitId, overrides) => {
  const response = await axiosAssessmentInstance.put(
    `${base(cid)}/enablements/${globalBenefitId}`,
    overrides
  );
  return unwrap(response);
};

export const disableGlobalBenefit = async (cid, globalBenefitId) => {
  const response = await axiosAssessmentInstance.delete(
    `${base(cid)}/enablements/${globalBenefitId}`
  );
  return unwrap(response);
};

// ─── Utilization analytics ──────────────────────────────────────────────────

export const getBenefitUtilization = async (cid, { since, until } = {}) => {
  const response = await axiosAssessmentInstance
    .get(`${base(cid)}/utilization`, { params: { since, until } })
    .catch(
      swallow404({
        data: {
          payload: { rows: [], total_offers: 0, range_start: since, range_end: until },
        },
      })
    );
  return unwrap(response, { rows: [], total_offers: 0 });
};
